import React, { useEffect, useRef } from 'react';
import './ImageGallery.css';

interface ImageGalleryProps {
  images: string[];
  title?: string;
  onImageChange?: (index: number) => void;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  title,
  onImageChange
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // 检测是否为移动端的函数
    const isMobile = () => window.innerWidth <= 768;

    // 初始设置
    let currentIsMobile = isMobile();

    // 如果是移动端，不添加自定义滚动处理，使用原生滚动
    if (currentIsMobile) {
      // 窗口大小变化监听器
      const handleResize = () => {
        const newIsMobile = isMobile();
        if (newIsMobile !== currentIsMobile) {
          currentIsMobile = newIsMobile;
          
          // 如果切换到桌面模式，需要重新加载来重新初始化
          if (!currentIsMobile) {
            window.location.reload();
          }
        }
      };

      window.addEventListener('resize', handleResize);

      return () => {
        // 移动端清理函数
        window.removeEventListener('resize', handleResize);
      };
    }

    // 桌面端的超级丝滑滚动处理
    let animationId: number;

    const handleScroll = (e: WheelEvent) => {
      e.preventDefault();

      // 取消之前的动画
      if (animationId) {
        cancelAnimationFrame(animationId);
      }

      const delta = e.deltaY || e.deltaX;
      const scrollAmount = delta * 0.8; // 减小滚动量，让滚动更精细

      // 直接设置滚动位置，更加即时响应
      const newScrollLeft = Math.max(0,
        Math.min(container.scrollLeft + scrollAmount,
          container.scrollWidth - container.clientWidth)
      );

      // 使用更简单直接的滚动方式
      animationId = requestAnimationFrame(() => {
        container.scrollLeft = newScrollLeft;
      });
    };

    // 简化的触摸处理 - 更直接更流畅（仅桌面端）
    let touchStartX = 0;
    let touchStartY = 0;
    let isTouchScrolling = false;
    let lastTouchX = 0;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      lastTouchX = touchStartX;
      isTouchScrolling = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      const currentX = touch.clientX;
      const currentY = touch.clientY;

      // 判断滚动方向
      if (!isTouchScrolling) {
        const deltaX = Math.abs(currentX - touchStartX);
        const deltaY = Math.abs(currentY - touchStartY);

        if (deltaX > deltaY && deltaX > 10) {
          isTouchScrolling = true;
        }
      }

      if (isTouchScrolling) {
        e.preventDefault();

        const deltaX = lastTouchX - currentX;

        // 直接滚动，更即时
        const newScrollLeft = Math.max(0,
          Math.min(container.scrollLeft + deltaX,
            container.scrollWidth - container.clientWidth)
        );

        container.scrollLeft = newScrollLeft;
        lastTouchX = currentX;
      }
    };

    const handleTouchEnd = () => {
      isTouchScrolling = false;
    };

    // 键盘导航优化（仅桌面端）
    const handleKeyDown = (e: KeyboardEvent) => {
      const scrollDistance = 400;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          container.scrollBy({ left: -scrollDistance, behavior: 'smooth' });
          break;
        case 'ArrowRight':
          e.preventDefault();
          container.scrollBy({ left: scrollDistance, behavior: 'smooth' });
          break;
        case 'ArrowUp':
        case 'ArrowDown':
          e.preventDefault();
          const direction = e.key === 'ArrowDown' ? 1 : -1;
          container.scrollBy({ left: scrollDistance * direction, behavior: 'smooth' });
          break;
        case 'Work':
          e.preventDefault();
          container.scrollTo({ left: 0, behavior: 'smooth' });
          break;
        case 'End':
          e.preventDefault();
          container.scrollTo({ left: container.scrollWidth, behavior: 'smooth' });
          break;
      }
    };

    // 窗口大小变化监听器（桌面端）
    const handleResize = () => {
      const newIsMobile = isMobile();
      if (newIsMobile !== currentIsMobile) {
        currentIsMobile = newIsMobile;
        
        // 如果切换到移动模式，需要重新加载来切换到移动端逻辑
        if (currentIsMobile) {
          window.location.reload();
        }
      }
    };

    // 添加事件监听器（仅桌面端）
    container.addEventListener('wheel', handleScroll, { passive: false });
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);

    // 清理函数（桌面端）
    return () => {
      container.removeEventListener('wheel', handleScroll);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, []);

  if (!images || images.length === 0) {
    return (
      <div className="image-gallery">
        <div className="no-images-container">
          <div className="no-images-message">
            No images available
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="image-gallery" ref={containerRef}>
      <div className="horizontal-scroll-container">
        {images.map((image, index) => (
          <div
            key={`${image}-${index}`}
            className="image-item"
          >
            <img
              src={image}
              alt={`${title || 'Gallery'} - Image ${index + 1}`}
              className="gallery-image"
              loading="lazy"
              referrerPolicy="no-referrer"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ImageGallery;